'use client'

import { useFormListener } from '@components/hooks/useFormListener/index.client'
import { usePrevious } from '@components/hooks/usePrevious'
import { classMerge } from '@components/utilities/classMerge'
import { useScrollAnimationsForRef } from '@lib/animation/useScrollAnimationsForRef'
import { useEffect, useRef } from 'react'
import { TRIAL_APPLY_FORM_ID } from './TrialApplyFooter.client'

export default function TrialApplyQuestionContainer({
  children,
  childrenClassName,
}: {
  children: React.ReactNode
  childrenClassName?: string
}) {
  const questionContainerRef = useRef(null)
  const { scrollIn, scrollOut } =
    useScrollAnimationsForRef(questionContainerRef)

  const previousFormStatus = usePrevious(
    useFormListener(TRIAL_APPLY_FORM_ID).formStatus,
  )

  useEffect(() => {
    const form = document.getElementById(TRIAL_APPLY_FORM_ID)
    if (form) {
      form.addEventListener('submit', scrollOut)
    }
    scrollIn()
    return () => form?.removeEventListener('submit', scrollOut)
  }, [])

  useEffect(() => {
    // In the case of a successful form submission:
    // User clicks submit -> form is pending and scrolled out ->
    // server responds with a 303 and we redirect to new page
    // new form is remounted, this block is not run
    // In the case of an error:
    // User clicks submit -> form is pending and scrolled out ->
    // server responds but does not redirect -> form status changes from pending to "valid" ->
    //  we scroll back in the existing form
    if (previousFormStatus === 'pending') {
      scrollIn()
    }
  }, [previousFormStatus])

  return (
    <div
      className='hide-scrollbars w-full max-w-6xl justify-center overflow-y-auto px-4 pb-48 opacity-0 md:px-8'
      ref={questionContainerRef}
    >
      <div className={classMerge('mx-auto', childrenClassName)}>{children}</div>
    </div>
  )
}
